import { useEffect, useMemo, useRef } from 'react';
import { isNil } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { REALLOCATION_FAILED_DETAIL, REALLOCATION_FAILED_TOAST_DURATION_MS } from 'common/constants/allocations';
import type { SeenReallocationFailure, UseReallocationFailureAlertParams } from './useReallocationFailureAlert.types';

// Whether the selected measurement date has a failed reallocation, toasting when one appears.
const useReallocationFailureAlert = ({
  companyMeasurementDateList,
  currentMeasurementDate,
}: UseReallocationFailureAlertParams): boolean => {
  // Pinned date if in this company's list, else the most recent (as useCompanyMeasurementDateFilter).
  const selectedMeasurementDate = useMemo(() => {
    if (!companyMeasurementDateList?.length) {
      return undefined;
    }
    const pinnedMeasurementDate = currentMeasurementDate?.slug
      ? companyMeasurementDateList.find(md => md.id === currentMeasurementDate.id)
      : undefined;
    return pinnedMeasurementDate ?? companyMeasurementDateList[0];
  }, [companyMeasurementDateList, currentMeasurementDate]);
  const reallocationFailedAt = selectedMeasurementDate?.reallocation_failed_at ?? null;

  // Toast only for a failure that appears while the date is open; existing ones get the badge alone.
  const seenReallocationFailureRef = useRef<SeenReallocationFailure | null>(null);
  useEffect(() => {
    if (!selectedMeasurementDate) {
      return;
    }
    const seen = seenReallocationFailureRef.current;
    if (
      seen?.measurementDateId === selectedMeasurementDate.id &&
      reallocationFailedAt &&
      reallocationFailedAt !== seen?.failedAt
    ) {
      enqueueSnackbar(REALLOCATION_FAILED_DETAIL, {
        autoHideDuration: REALLOCATION_FAILED_TOAST_DURATION_MS,
        variant: 'error',
      });
    }
    seenReallocationFailureRef.current = {
      failedAt: reallocationFailedAt,
      measurementDateId: selectedMeasurementDate.id,
    };
  }, [reallocationFailedAt, selectedMeasurementDate]);

  return !isNil(reallocationFailedAt);
};

export default useReallocationFailureAlert;
